import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

export default function DeFiCalculator() {
  const [activeTab, setActiveTab] = useState('slippage');
  const [results, setResults] = useState({});

  // Slippage Calculator
  const [slippage, setSlippage] = useState({
    inputAmount: 1000,
    outputAmount: 950,
    slippagePercent: 5
  });

  // Yield Calculator
  const [yield_, setYield] = useState({
    principal: 10000,
    rate: 8,
    time: 1,
    compoundFreq: 12,
    result: 0
  });

  // Fee Calculator
  const [fees, setFees] = useState({
    transactionAmount: 1000,
    gasPrice: 50,
    gasUnits: 21000,
    platformFee: 0.3,
    results: {}
  });

  // Staking APY Calculator
  const [staking, setStaking] = useState({
    stakeAmount: 1000,
    apy: 12,
    stakingMonths: 12,
    result: 0
  });

  const calculateSlippage = () => {
    const slip = ((slippage.inputAmount - slippage.outputAmount) / slippage.inputAmount) * 100;
    setSlippage(prev => ({ ...prev, slippagePercent: slip.toFixed(2) }));
  };

  const calculateYield = () => {
    const A = yield_.principal * Math.pow(1 + (yield_.rate / 100) / yield_.compoundFreq, yield_.compoundFreq * yield_.time);
    const interest = A - yield_.principal;
    setYield(prev => ({ ...prev, result: interest.toFixed(2) }));
  };

  const calculateFees = () => {
    const gasFee = (fees.gasPrice * fees.gasUnits) / 1e9; // Convert to ETH
    const platformFee = (fees.transactionAmount * fees.platformFee) / 100;
    const totalFee = gasFee + platformFee;
    const netOutput = fees.transactionAmount - totalFee;

    setFees(prev => ({
      ...prev,
      results: {
        gasFee: gasFee.toFixed(4),
        platformFee: platformFee.toFixed(2),
        totalFee: totalFee.toFixed(4),
        netOutput: netOutput.toFixed(2)
      }
    }));
  };

  const calculateStaking = () => {
    const finalAmount = staking.stakeAmount * Math.pow(1 + (staking.apy / 100), staking.stakingMonths / 12);
    const rewards = finalAmount - staking.stakeAmount;
    setStaking(prev => ({ ...prev, result: rewards.toFixed(2) }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">📊 DeFi Calculator</h1>
          <p className="text-gray-300">Calculate yields, fees, slippage, and staking rewards</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {['slippage', 'yield', 'fees', 'staking'].map((tab) => (
            <Button
              key={tab}
              onClick={() => setActiveTab(tab)}
              variant={activeTab === tab ? 'default' : 'outline'}
              className="whitespace-nowrap"
            >
              {tab === 'slippage' && '💧 Slippage'}
              {tab === 'yield' && '📈 Yield'}
              {tab === 'fees' && '💰 Fees'}
              {tab === 'staking' && '🎯 Staking'}
            </Button>
          ))}
        </div>

        {/* Slippage Calculator */}
        {activeTab === 'slippage' && (
          <Card className="p-8 bg-slate-800 border-slate-700">
            <h2 className="text-2xl font-bold text-white mb-6">💧 Slippage Calculator</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="text-gray-300 block mb-2">Input Amount ($)</label>
                <Input
                  type="number"
                  value={slippage.inputAmount}
                  onChange={(e) => setSlippage({ ...slippage, inputAmount: parseFloat(e.target.value) })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-gray-300 block mb-2">Output Amount ($)</label>
                <Input
                  type="number"
                  value={slippage.outputAmount}
                  onChange={(e) => setSlippage({ ...slippage, outputAmount: parseFloat(e.target.value) })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>
            <Button onClick={calculateSlippage} className="w-full mb-6">Calculate Slippage</Button>
            {slippage.slippagePercent && (
              <Card className="p-6 bg-slate-700 border-slate-600">
                <p className="text-gray-300 mb-2">Slippage:</p>
                <p className="text-3xl font-bold text-yellow-400">{slippage.slippagePercent}%</p>
                <p className="text-sm text-gray-400 mt-2">This is the price difference between expected and actual output.</p>
              </Card>
            )}
          </Card>
        )}

        {/* Yield Calculator */}
        {activeTab === 'yield' && (
          <Card className="p-8 bg-slate-800 border-slate-700">
            <h2 className="text-2xl font-bold text-white mb-6">📈 Compound Yield Calculator</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="text-gray-300 block mb-2">Principal ($)</label>
                <Input
                  type="number"
                  value={yield_.principal}
                  onChange={(e) => setYield({ ...yield_, principal: parseFloat(e.target.value) })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-gray-300 block mb-2">Annual Rate (%)</label>
                <Input
                  type="number"
                  value={yield_.rate}
                  onChange={(e) => setYield({ ...yield_, rate: parseFloat(e.target.value) })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-gray-300 block mb-2">Years</label>
                <Input
                  type="number"
                  value={yield_.time}
                  onChange={(e) => setYield({ ...yield_, time: parseFloat(e.target.value) })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-gray-300 block mb-2">Compound Frequency (per year)</label>
                <Input
                  type="number"
                  value={yield_.compoundFreq}
                  onChange={(e) => setYield({ ...yield_, compoundFreq: parseFloat(e.target.value) })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>
            <Button onClick={calculateYield} className="w-full mb-6">Calculate Yield</Button>
            {yield_.result > 0 && (
              <Card className="p-6 bg-slate-700 border-slate-600">
                <p className="text-gray-300 mb-2">Interest Earned:</p>
                <p className="text-3xl font-bold text-green-400">${yield_.result}</p>
                <p className="text-sm text-gray-400 mt-2">Total: ${(yield_.principal + parseFloat(yield_.result)).toFixed(2)}</p>
              </Card>
            )}
          </Card>
        )}

        {/* Fee Calculator */}
        {activeTab === 'fees' && (
          <Card className="p-8 bg-slate-800 border-slate-700">
            <h2 className="text-2xl font-bold text-white mb-6">💰 Fee Calculator</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="text-gray-300 block mb-2">Transaction Amount ($)</label>
                <Input
                  type="number"
                  value={fees.transactionAmount}
                  onChange={(e) => setFees({ ...fees, transactionAmount: parseFloat(e.target.value) })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-gray-300 block mb-2">Gas Price (Gwei)</label>
                <Input
                  type="number"
                  value={fees.gasPrice}
                  onChange={(e) => setFees({ ...fees, gasPrice: parseFloat(e.target.value) })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-gray-300 block mb-2">Gas Units</label>
                <Input
                  type="number"
                  value={fees.gasUnits}
                  onChange={(e) => setFees({ ...fees, gasUnits: parseFloat(e.target.value) })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-gray-300 block mb-2">Platform Fee (%)</label>
                <Input
                  type="number"
                  value={fees.platformFee}
                  onChange={(e) => setFees({ ...fees, platformFee: parseFloat(e.target.value) })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>
            <Button onClick={calculateFees} className="w-full mb-6">Calculate Fees</Button>
            {fees.results.totalFee && (
              <div className="space-y-3">
                <Card className="p-4 bg-slate-700 border-slate-600">
                  <p className="text-gray-300">Gas Fee</p>
                  <p className="text-xl font-bold text-gray-200">Ξ {fees.results.gasFee}</p>
                </Card>
                <Card className="p-4 bg-slate-700 border-slate-600">
                  <p className="text-gray-300">Platform Fee</p>
                  <p className="text-xl font-bold text-gray-200">${fees.results.platformFee}</p>
                </Card>
                <Card className="p-4 bg-red-900 border-red-700">
                  <p className="text-gray-300">Total Fees</p>
                  <p className="text-2xl font-bold text-red-200">${fees.results.totalFee}</p>
                </Card>
                <Card className="p-4 bg-green-900 border-green-700">
                  <p className="text-gray-300">Net Output</p>
                  <p className="text-2xl font-bold text-green-200">${fees.results.netOutput}</p>
                </Card>
              </div>
            )}
          </Card>
        )}

        {/* Staking Calculator */}
        {activeTab === 'staking' && (
          <Card className="p-8 bg-slate-800 border-slate-700">
            <h2 className="text-2xl font-bold text-white mb-6">🎯 Staking Rewards Calculator</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="text-gray-300 block mb-2">Stake Amount</label>
                <Input
                  type="number"
                  value={staking.stakeAmount}
                  onChange={(e) => setStaking({ ...staking, stakeAmount: parseFloat(e.target.value) })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-gray-300 block mb-2">Annual APY (%)</label>
                <Input
                  type="number"
                  value={staking.apy}
                  onChange={(e) => setStaking({ ...staking, apy: parseFloat(e.target.value) })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-gray-300 block mb-2">Staking Period (months)</label>
                <Input
                  type="number"
                  value={staking.stakingMonths}
                  onChange={(e) => setStaking({ ...staking, stakingMonths: parseFloat(e.target.value) })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>
            <Button onClick={calculateStaking} className="w-full mb-6">Calculate Rewards</Button>
            {staking.result > 0 && (
              <Card className="p-6 bg-slate-700 border-slate-600">
                <p className="text-gray-300 mb-2">Staking Rewards:</p>
                <p className="text-3xl font-bold text-green-400">${staking.result}</p>
                <p className="text-sm text-gray-400 mt-2">Total: ${(staking.stakeAmount + parseFloat(staking.result)).toFixed(2)}</p>
              </Card>
            )}
          </Card>
        )}

        {/* Back Button */}
        <div className="mt-8">
          <Button onClick={() => window.location.href = '/games'} size="lg" variant="outline" className="w-full">
            Back to Games
          </Button>
        </div>
      </div>
    </div>
  );
}
